/**
 * Раздел «Продажи»: менеджеры, клиенты, каналы и юрлица.
 *
 * Работает внутри того же контекста, что финансовый блок: та же панель
 * периода и города, тот же кеш ответов, та же панель детализации клиента.
 * Поэтому цифры здесь и в «Маржинальности» совпадают до рубля — они
 * получены одинаковыми запросами.
 */

import { useMemo, useState, type ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import type { CurrentUser } from '../types'
import { FinanceContext, type DrillTarget, type FinanceContextValue } from '../finance/context'
import { useCatalog } from '../finance/data'
import { Drawer, type DrawerOrigin } from '../finance/Drawer'
import { DrillContent, drillSubtitle, drillTitle } from '../finance/Drill'
import { reportRefs, Toolbar, useSectionNavigator } from '../finance/FinanceBlock'
import { buildContext, ClosedSection, FrameNotices, useBlockFrame } from '../finance/frame'
import { formatSelection, labelOf } from '../finance/period'
import '../finance/finance.css'
import './insights.css'
import { SALES_SECTIONS, salesPath, visibleSalesSections, type SalesSection } from './sections'
import { useInsightsNavigator } from './InsightsNav'
import { Managers, ManagerDrill } from './sales/Managers'
import { Customers } from './sales/Customers'
import { Channels } from './sales/Channels'
import { SectionTabs, BlockSkeleton } from './parts'

export type SalesDrill = DrillTarget | { kind: 'manager'; value: string; label: string }

export function useSalesSection(): SalesSection {
  const { section } = useParams()
  return (SALES_SECTIONS.find((item) => item.code === section)?.code ?? 'managers') as SalesSection
}

export function SalesBlock({ user }: { user: CurrentUser }) {
  const section = useSalesSection()
  const { section: requested } = useParams()
  const catalog = useCatalog(user)
  const goFinance = useSectionNavigator()
  const goInsights = useInsightsNavigator()
  const sections = visibleSalesSections(user)
  // Менеджеры и клиенты построены на ABC — его покрытие и берём первым.
  const frame = useBlockFrame(user, catalog, ['abc_customers', 'margin_by_department', 'sales_by_group'])
  const { selection, branch, branches, restricted, scopeLabel, patch } = frame

  const [drill, setDrill] = useState<{ target: SalesDrill; origin: DrawerOrigin } | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const value = useMemo<FinanceContextValue>(
    () =>
      buildContext(frame, user, catalog, {
        receivableFilter: {},
        go: goFinance,
        drill: (target, origin) => {
          setDrill({ target, origin })
          setDrawerOpen(true)
        },
      }),
    [user, catalog, selection, branch, frame.branchName, scopeLabel, restricted, goFinance, frame.coverage, frame.branches],
  )

  if (!sections.some((item) => item.code === section)) {
    if (requested && SALES_SECTIONS.some((item) => item.code === requested)) {
      const closed = SALES_SECTIONS.find((item) => item.code === requested)!
      const first = sections[0]
      return (
        <ClosedSection
          title={closed.title}
          sectionCode={`sales.${closed.code}`}
          reports={closed.code === 'channels' ? reportRefs('margin_by_department', 'sales_by_group') : reportRefs('abc_customers')}
          user={user}
          fallback={first ? { path: salesPath(first.code), label: `Открыть «${first.title}»` } : { path: '/', label: 'На главную' }}
        />
      )
    }
    return sections.length > 0 ? <Navigate to={salesPath(sections[0]!.code)} replace /> : <Navigate to="/" replace />
  }

  const openManager = (target: { value: string; label: string }, origin: DrawerOrigin) => {
    setDrill({ target: { kind: 'manager', ...target }, origin })
    setDrawerOpen(true)
  }

  let body: ReactNode
  if (!catalog.ready) body = <BlockSkeleton />
  else if (section === 'customers') body = <Customers />
  else if (section === 'channels') body = <Channels />
  else body = <Managers onOpenManager={openManager} />

  const target = drill?.target
  return (
    <FinanceContext.Provider value={value}>
      <div className="fin">
        <Toolbar
          selection={selection}
          onSelection={(next) => patch({ p: formatSelection(next) })}
          branch={branch}
          branches={branches}
          restricted={restricted}
          onBranch={(next) => patch({ b: next })}
          periodApplies
          lastSales={frame.lastSales}
        />
        <FrameNotices notices={frame.notices} onDismiss={frame.dismissBranchNotice} />
        <SectionTabs
          label="Разделы продаж"
          items={sections.map((item) => ({ path: salesPath(item.code), short: item.short, active: item.code === section }))}
          onGo={goInsights}
        />
        <div className="fin-section" key={section}>
          {body}
        </div>
      </div>

      <Drawer
        open={drawerOpen}
        origin={drill?.origin ?? null}
        onClose={() => setDrawerOpen(false)}
        title={target ? (target.kind === 'manager' ? target.label : drillTitle(target)) : ''}
        subtitle={target ? `${target.kind === 'manager' ? 'Менеджер' : drillSubtitle(target)} · ${labelOf(selection)} · ${scopeLabel}` : undefined}
      >
        {target ? target.kind === 'manager' ? <ManagerDrill value={target.value} /> : <DrillContent target={target} /> : null}
      </Drawer>
    </FinanceContext.Provider>
  )
}
